import { css } from "@emotion/react"
import IndexResultCard from "./IndexResultCard"
import { NavLink, useParams } from "react-router"
import PageTitle from "../_general/PageTitle"
import { useIntl } from "react-intl"
import PitchContext from "../../context/Pitch"
import LoaderText from "../_general/LoaderText"
import { colors, fonts } from "shared-ui"
import AnalysisDate from "../analysis/AnalysisDate"
import useRawState from "../../hooks/useRawState"
import TestResultCard from "../test/TestResultCard"
import Loader from "../_general/Loader"
import PageSubtitle from "../_general/PageSubtitle"
import ProfileContext from "../../context/Profile"
import { useEffect, useState, useContext } from "react"
import useRawPromise from "../../hooks/useRawPromise"

export default function IndexDetailPage({ ...props }) {

    const { formatMessage } = useIntl()
    const { data: pitch } = useContext(PitchContext)
    const { data: profile } = useContext(ProfileContext)
    const allowPerformanceFeature = profile?.featureFlags?.includes('performance')

    const { indexId } = useParams()

    const { data: indexData, isBusy: indexDataLoading } = useRawState(`/api/current/index-results/${indexId}`)

    const { get } = useRawPromise()

    const [metaDataTests, setMetaDataTests] = useState()

    useEffect(() => {

        const fetchTests = async (indexData) => {
            const testsBuffer = []

            if (Array.isArray(indexData?.metaData)) {
                for (const test of indexData.metaData) {
                    try {
                        const { data: testData } = await get(`/api/current/tests/${test.test_id}`)
                        testsBuffer.push({
                            ...testData,
                            analysisId: test.analysis_id,
                        })
                    } catch (e) {
                        console.log(e)
                    }
                }
            }
            setMetaDataTests(testsBuffer)
        }


        if (indexData) {
            fetchTests(indexData)
        }

    }, [indexData])

    if (!allowPerformanceFeature) return null
    if (indexDataLoading) return <Loader size={'2em'} />
    if (!indexData) return null
    return <div css={css`max-width: 2200px;`}  {...props}  >
        <div css={style.header} >
            <PageTitle css={style.title}>
                {pitch
                    ?
                    <NavLink
                        css={[style.link, css`cursor: ${!!pitch?.club?.id ? 'pointer' : 'unset'};`]}
                        to={!!pitch?.club?.id ? `/club/${pitch?.club?.id}` : undefined}>
                        {pitch?.club?.name || formatMessage({ id: 'noClub' })}
                    </NavLink>
                    :
                    <LoaderText subtle />
                }
                &nbsp;{'/'}&nbsp;
                {pitch
                    ?
                    <NavLink
                        css={style.link}
                        to={`/analyses?pitch=${pitch.id}`}>
                        {pitch.name}
                    </NavLink>
                    :
                    <LoaderText subtle />
                }
                &nbsp;{'/'}&nbsp;

                <NavLink
                    css={style.link}
                    to={`/pitch/${pitch?.id}/index${indexData.latestTestTimestamp ? '?timestamp=' + indexData.latestTestTimestamp : ''} `}>
                    {formatMessage({ id: 'index' })}
                </NavLink>

                &nbsp;{'/'}&nbsp;
                <span css={css`color: ${colors.main1};`}>
                    {indexData.indexCategory.name}&nbsp;-&nbsp;
                    {<AnalysisDate
                        date={indexData.latestTestTimestamp}
                        timeZone={pitch?.venue?.timeZone}
                        css={style.analysisDate}
                    />}
                </span>
            </PageTitle>
        </div>

        <IndexResultCard noNavigate={true} indexData={indexData} />

        <PageSubtitle css={style.subTitle}>
            {formatMessage({ id: "relatedTests" })}
        </ PageSubtitle>

        <div css={style.testGrid}>
            {metaDataTests
                ?.toSorted((a, b) => (a.testCategory.weight < b.testCategory.weight) ? -1 : 1)
                ?.map(test => {
                    return <TestResultCard
                        key={test.id}
                        test={test}
                        showDetails={true}
                        showAnalysisDate={true}
                        navigateToAnalysis={true}
                        analysisId={test.analysisId}
                    />
                })
                ||
                <Loader size='2em' />
            }
        </div>
    </div>
}

const style = {
    header: css`
        display: flex; 
        justify-content: space-between; 
        align-items: center; 
        margin-bottom: 2em;
        flex-wrap: wrap;
    `,
    title: css`
        margin-bottom: 0; 
        display: flex; 
        flex-wrap: wrap;
    `,
    analysisDate: css`
        font-family: ${fonts.main};
        font-weight: unset;
        font-size: 1em;
    `,
    testGrid: css`
        display: grid;
        gap: 0.5em;
        grid-template-columns: repeat(2, 1fr);
        @media (max-width: 1720px) {
            grid-template-columns: repeat(1, 1fr)
        }
    `,
    subTitle: css`
        height: min-content;
        grid-column: span 2;
        margin-top: 1em;
    `,
    link: css`
        text-decoration: none; 
        color: inherit;
    `
}